<template>
  <div>
    <navbar :links="links" />
    <!-- Loading indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <div v-if="channel">
      <v-list
        avatar
        :dense="$vuetify.breakpoint.width < 400"
      >
        <v-list-tile
          avatar
          class="no-hover-clr no-click-pointer"
          :ripple="false"
        >
          <v-list-tile-avatar>
            <v-icon
              large
              class="msr--text"
            >
              volume_up
            </v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              <strong
                class="grey--text text--darken-3"
                style="white-space: initial;"
              >{{ channel.name }}</strong>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
      <v-layout
        v-bind="channelLayout"
        justify-center
        :align-center="channelLayout.column"
        class="mt-2"
      >
        <div :class="{'mr-4': channelLayout.row}">
          <img
            v-if="channel.cover"
            :src="channel.cover"
            width="240"
            height="240"
          >
          <v-icon
            v-else
            style="font-size: 8em; width: 240px; height: 240px;"
          >
            music_note
          </v-icon>
        </div>
        <v-layout
          column
          :class="{'mt-4': channelLayout.column, 'mt-2': channelLayout.row}"
        >
          <!-- Sample .mp3 -->
          <v-layout
            v-if="channel.sample"
            column
            class="mb-3"
          >
            <v-subheader class="pa-0 mb-2 auto-height">
              {{ $t('radioChannelSample') }}
            </v-subheader>
            <audio
              :src="channel.sample"
              preload="metadata"
              controls
              controlslist="nodownload"
              :class="{'full-width': channelLayout.row}"
            />
          </v-layout>
          <blockquote class="blockquote pt-0 px-0">
            {{ channel.description }}
          </blockquote>
          <v-spacer />
        </v-layout>
      </v-layout>
      <!-- Active on Decoders -->
      <list-header
        :label="usageHeaderLabel"
        :show-expander="channel.decoders.length > 0"
        :expanded.sync="allStoreWidgetsExpanded"
        :disable-spacers="channel.decoders.length === 0"
        data-cy="radiochannel-detail-list-header"
      >
        <div
          v-if="channel.decoders.length === 0"
          slot="label-suffix"
        >
          &nbsp;-
          <a
            href="javascript:void(0)"
            @click="goToAddDecoderToChannel(channelId)"
          >{{ $t('activateContentNow') }}</a>
        </div>
      </list-header>
      <v-list
        dense
        subheader
      >
        <template v-for="(wrapper, wrapperIndex) in orderedStoreWrappers">
          <!-- Store -->
          <store-tile
            :key="'store_' + wrapper.store.id"
            :store="wrapper.store"
            :show-divider="wrapperIndex < orderedStoreWrappers.length - 1"
            :expanded="wrapper.expanded"
            @click="toggleWrapperExpanded(wrapper)"
          />
          <!-- Decoders -->
          <transition
            :key="'storedecoders_' + wrapper.store.id"
            name="list-transition"
            mode="out-in"
          >
            <v-list
              v-for="decoder in (wrapper.expanded ? wrapper.decoders : [])"
              :key="'decoder_' + decoder.id"
              dense
              avatar
              class="pa-0 inset-left-within-container"
            >
              <v-list-tile
                class="channeldetail-values channeldetail-tile mt-1 list-tile-align-start"
                :ripple="false"
                @click="goToDecoder(decoder)"
              >
                <v-list-tile-avatar class="smaller-avatar clickable">
                  <v-icon>radio</v-icon>
                </v-list-tile-avatar>
                <div class="channeldetail-item-group">
                  <div class="channeldetail-item clickable">
                    <v-list-tile-title style="margin-top: 12px;">
                      {{ decoder.description }}
                    </v-list-tile-title>
                  </div>
                </div>
                <v-spacer />
                <v-list-tile-avatar style="min-width: 0; max-width: 32px;">
                  <v-icon
                    medium
                    class="grey--text"
                  >
                    chevron_right
                  </v-icon>
                </v-list-tile-avatar>
              </v-list-tile>
            </v-list>
          </transition>
        </template>
      </v-list>
      <!-- Add Decoders button -->
      <v-slide-y-reverse-transition>
        <v-btn
          class="msr"
          :class="$desktopModeStyles"
          fixed
          bottom
          right
          dark
          round
          @click="goToAddDecoderToChannel(channelId)"
        >
          <v-icon left>
            add
          </v-icon>
          {{ $t('addDecoderToChannel') }}
        </v-btn>
      </v-slide-y-reverse-transition>
      <!-- Empty placeholder to allow a bit more scrolling -->
      <div style="height: 100px;" />
    </div>
  </div>
</template>

<script>
/**
 * An object bundling a Store with its Decoders.
 *
 * We mainly need this object to remember expansion state.
 */
class StoreDecodersWrapper {
  constructor(store, decoders, expanded) {
    this.store = store;
    this.decoders = decoders;
    this.expanded = expanded;
  }
}

import groupBy from 'lodash.groupby';
import orderBy from 'lodash.orderby';

import ListHeader from '@/components/ListHeader';
import Navbar from '@/components/Navbar';
import StoreTile from '@/components/StoreTile';

export default {
  name: 'RadioChannelDetail',
  components: {
    ListHeader,
    Navbar,
    StoreTile,
  },
  data() {
    return {
      links: ['dashboard', 'music', 'radioChannel'],
      channel: null,
      loading: false,
      orderedStoreWrappers: [],
      allStoreWidgetsExpanded: false,
    };
  },
  computed: {
    channelId() {
      return this.$route.params.channelId;
    },
    channelLayout() {
      if (this.$vuetify.breakpoint.smAndUp) {
        return {row: true};
      }
      return {column: true};
    },
    usageHeaderLabel() {
      if (!this.channel.decoders.length) {
        return this.$t('radioChannelNotActiveAnywhere');
      }
      return this.$t('activeDecoders') + ': ' + this.channel.decoders.length;
    },
    storeIdToDecoders() {
      if (!this.channel) {
        return {};
      }
      return groupBy(this.channel.decoders, 'store.id');
    },
    orderedStores() {
      if (!this.channel) {
        return [];
      }
      const stores = this.channel.decoders.map((decoder) => decoder.store);
      return orderBy(stores, 'city');
    },
  },
  watch: {
    allStoreWidgetsExpanded(expanded) {
      this.orderedStoreWrappers.forEach((wrapper) => {
        wrapper.expanded = expanded;
      });
    },
  },
  mounted() {
    this.loading = true;
    this.fetchChannel().then(() => {
      this.createStoreWrappers();
      this.loading = false;
      this.updateNavbarLinks();
    });
  },
  methods: {
    createStoreWrappers() {
      let wrappers = [];
      const numStores = Object.keys(this.storeIdToDecoders).length;
      const initiallyExpanded = numStores === 1;

      Object.keys(this.storeIdToDecoders).forEach((storeId) => {
        const decoders = this.storeIdToDecoders[storeId];
        const store = decoders[0].store;
        const wrapper = new StoreDecodersWrapper(
          store,
          decoders,
          initiallyExpanded
        );
        wrappers.push(wrapper);
      });

      wrappers = orderBy(wrappers, 'store.city');
      this.orderedStoreWrappers = wrappers;
    },
    areAllWidgetsExpanded() {
      const expandedWrappers = this.orderedStoreWrappers.filter(
        (wrapper) => wrapper.expanded
      );
      return expandedWrappers.length === this.orderedStoreWrappers.length;
    },
    toggleWrapperExpanded(wrapper) {
      wrapper.expanded = !wrapper.expanded;
    },
    updateNavbarLinks() {
      const channelLink = {
        label: this.channel.name,
        active: false,
      };
      const links = ['dashboard', 'music', channelLink];
      this.links = links;
    },
    fetchChannel() {
      return this.$api.retrieveRadioChannel(this.channelId).then((response) => {
        this.channel = response.data;
      });
    },
    goToDecoder(decoder) {
      let path = '/player/' + decoder.ctype + '/' + decoder.id;
      this.$router.push(path);
    },
    goToAddDecoderToChannel(channelId) {
      let path = '/channel/' + channelId + '/add';
      this.$router.push(path);
    },
  },
};
</script>

<style>
/* Ensure full width and adaptive height for v-list-tile. */
.channeldetail-tile .v-list__tile {
  width: 100%;
  height: auto !important;
}
</style>

<style>
.channeldetail-values {
  display: flex;
}

.channeldetail-item {
  margin: 0.1em 0 0.1em 0;
}
.channeldetail-item.last {
  margin-right: 0;
}

.channeldetail-item-group {
  display: flex;
  flex-flow: row nowrap;
}
.channeldetail-item-group.last {
  justify-content: flex-end;
  align-items: flex-start;
}
@media (max-width: 959px) {
  .channeldetail-item-group {
    flex-flow: column nowrap;
  }
  .channeldetail-item-group.last {
    justify-content: flex-start;
    align-items: flex-end;
  }
}

.list {
  transform-origin: top;
}

.list-transition-enter-active,
.list-transition-leave-active {
  transition: all 0.2s ease-in-out;
}
.list-transition-enter,
.list-transition-leave-to {
  opacity: 0;
  transform: scaleY(0);
}
</style>



// WEBPACK FOOTER //
// src/pages/RadioChannelDetail.vue